import { Button } from "@chakra-ui/button";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Input, InputGroup, InputRightElement } from "@chakra-ui/input";
import { VStack, Text } from "@chakra-ui/layout";
import { useToast } from "@chakra-ui/toast";
import axios from "axios";
import { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { ChatState } from "../../Context/ChatProvider";
import VerifyOtp from "./VerifyOtp";

const strengthOf = (pw) => {
  if (!pw) return { pct: 0, label: "", color: "transparent" };
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (score <= 2) return { pct: 33, label: "Weak — go harder", color: "#f87171" };
  if (score <= 3) return { pct: 62, label: "Solid — almost elite", color: "#fbbf24" };
  return { pct: 100, label: "Beast mode 🔥", color: "#34d399" };
};

const Signup = () => {
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);
  const toast = useToast();
  const history = useHistory();
  const { setUser } = ChatState();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmpassword, setConfirmpassword] = useState("");
  const [password, setPassword] = useState("");
  const [pic, setPic] = useState();
  const [picLoading, setPicLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form");
  const [pendingEmail, setPendingEmail] = useState("");
  const [devPreview, setDevPreview] = useState("");

  const strength = useMemo(() => strengthOf(password), [password]);
  const matchState =
    !confirmpassword || !password
      ? null
      : password === confirmpassword
      ? { ok: true, msg: "Passwords match ✓" }
      : { ok: false, msg: "Passwords don't match yet" };

  const submitHandler = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    if (!name || !email || !password || !confirmpassword) {
      toast({
        title: "Please Fill all the Fields",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
      return;
    }
    if (password !== confirmpassword) {
      toast({
        title: "Passwords Do Not Match",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
      return;
    }
    try {
      const config = {
        headers: {
          "Content-type": "application/json",
        },
      };
      const { data } = await axios.post(
        "/api/user",
        {
          name,
          email,
          password,
          pic,
        },
        config
      );
      if (data.requiresVerification) {
        setPendingEmail(data.email || email.trim().toLowerCase());
        setDevPreview(data.devPreviewOtp || "");
        setStep("otp");
        toast({
          title: data.emailSent === false ? "Account saved — email hiccup" : "Check your email",
          description:
            data.emailSent === false
              ? "We couldn't send the code. Tap Resend for a fresh one."
              : `We sent a 6-digit code to ${data.email || email}. Enter it to finish.`,
          status: data.emailSent === false ? "warning" : "success",
          duration: 6000,
          isClosable: true,
          position: "bottom",
        });
        setLoading(false);
        return;
      }
      toast({
        title: "Registration Successful",
        description: `Welcome to the loud side, ${data.name.split(" ")[0]}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      localStorage.setItem("userInfo", JSON.stringify(data));
      setUser(data);
      setLoading(false);
      history.push("/chats");
    } catch (error) {
      const payload = error.response?.data || {};
      if (payload.requiresVerification) {
        setPendingEmail(payload.email || email.trim().toLowerCase());
        setStep("otp");
        toast({
          title: "Code already on its way",
          description: payload.message || "Enter the code we just sent.",
          status: "info",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
        setLoading(false);
        return;
      }
      toast({
        title: "Error Occured!",
        description: payload.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
    }
  };

  const postDetails = (pics) => {
    setPicLoading(true);
    if (pics === undefined) {
      toast({
        title: "Please Select an Image!",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setPicLoading(false);
      return;
    }
    if (pics.type === "image/jpeg" || pics.type === "image/png") {
      const data = new FormData();
      data.append("file", pics);
      data.append("upload_preset", "chat-app");
      data.append("cloud_name", "istiaquenuhash");
      fetch("https://api.cloudinary.com/v1_1/istiaquenuhash/image/upload", {
        method: "post",
        body: data,
      })
        .then((res) => res.json())
        .then((data) => {
          setPic(data.url.toString());
          setPicLoading(false);
          toast({
            title: "Avatar locked in 📸",
            status: "success",
            duration: 2500,
            isClosable: true,
            position: "bottom",
          });
        })
        .catch((err) => {
          console.log(err);
          setPicLoading(false);
        });
    } else {
      toast({
        title: "Please Select an Image!",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setPicLoading(false);
      return;
    }
  };

  return (
    step === "otp" ? (
      <VerifyOtp
        email={pendingEmail}
        devPreviewOtp={devPreview}
        onBack={() => setStep("form")}
        onVerified={(data) => {
          localStorage.setItem("userInfo", JSON.stringify(data));
          setUser(data);
          history.push("/chats");
        }}
      />
    ) : (
    <VStack spacing="12px" align="stretch" as="form" onSubmit={submitHandler}>
      <FormControl id="first-name" isRequired>
        <FormLabel className="auth-label">Name</FormLabel>
        <Input
          className="auth-input"
          placeholder="What should the room call you?"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </FormControl>
      <FormControl id="email" isRequired>
        <FormLabel className="auth-label">Email Address</FormLabel>
        <Input
          className="auth-input"
          type="email"
          placeholder="you@electric.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>
      <FormControl id="password" isRequired>
        <FormLabel className="auth-label">Password</FormLabel>
        <InputGroup size="md" className="auth-input-wrap">
          <Input
            className="auth-input"
            type={show ? "text" : "password"}
            placeholder="Make it unbreakable"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            pr="4.8rem"
          />
          <InputRightElement width="4.5rem" h="100%">
            <Button className="show-btn" h="1.75rem" size="sm" onClick={handleClick}>
              {show ? "Hide" : "Show"}
            </Button>
          </InputRightElement>
        </InputGroup>
        {password && (
          <>
            <div className="strength-track">
              <div
                className="strength-fill"
                style={{ width: `${strength.pct}%`, background: strength.color }}
              />
            </div>
            <div className="strength-label" style={{ color: strength.color }}>
              {strength.label}
            </div>
          </>
        )}
      </FormControl>
      <FormControl id="confirm-password" isRequired>
        <FormLabel className="auth-label">Confirm Password</FormLabel>
        <InputGroup size="md" className="auth-input-wrap">
          <Input
            className="auth-input"
            type={show ? "text" : "password"}
            placeholder="One more time"
            value={confirmpassword}
            onChange={(e) => setConfirmpassword(e.target.value)}
            pr="4.8rem"
            borderColor={
              matchState ? (matchState.ok ? "green.400 !important" : "red.400 !important") : undefined
            }
          />
          <InputRightElement width="4.5rem" h="100%">
            <Button className="show-btn" h="1.75rem" size="sm" onClick={handleClick}>
              {show ? "Hide" : "Show"}
            </Button>
          </InputRightElement>
        </InputGroup>
        {matchState && (
          <Text fontSize="11px" fontWeight="700" mt="6px" color={matchState.ok ? "green.300" : "red.300"}>
            {matchState.msg}
          </Text>
        )}
      </FormControl>
      <FormControl id="pic">
        <FormLabel className="auth-label">Avatar {pic ? "✓ uploaded" : "(optional)"}</FormLabel>
        <Input
          className="auth-input file-drop"
          type="file"
          p={1.5}
          height="48px"
          accept="image/jpeg,image/png"
          onChange={(e) => postDetails(e.target.files[0])}
        />
      </FormControl>
      <Button
        className="auth-submit"
        width="100%"
        style={{ marginTop: 16 }}
        type="submit"
        isLoading={loading || picLoading}
        loadingText={picLoading ? "Uploading avatar…" : "Creating your legend…"}
      >
        Create Account →
      </Button>
    </VStack>
    )
  );
};

export default Signup;
